// Design A ("Corporate Band") Purchase Order -- used ONLY for Print Preview
// (via PurchaseOrderPrint.jsx) when print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/purchaseOrderAdapter.js exactly,
// including the pending-approval gate and the trailing Payment Terms page
// (rendered with Design A's own .da-page shell, not the legacy
// po-payment-terms-page CSS).
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, formatToCompanyTimezone } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { formatPercent, resolveItemDiscountAmount, formatItemDiscountCell } from "../../../../../lib/designANumberFormat";
import { purchaseOrderRecordnWithDetailsApi } from "../../../../../apiservice/purchase";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- mirrors purchaseOrderAdapter.js exactly.
// Doesn't affect the separate trailing Payment Terms page below.
const PURCHASE_ORDER_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };
const isBonusItem = (item) => Number(item.item_rate) === 0;

// Same fixed-layout column widths as purchaseOrderAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
// disc bumped from 64 to 72 -- the header is mode-neutral ("Disc") now so
// needs less room than "DISC %" did, but a flat-amount discount value
// (e.g. "1,234.50") needs more room than a 1-2 digit percentage ever did.
// rate/tax/total bumped up to the same safety margin validated on Sales
// Invoice (rate 100, tax 54, total/amount 104) -- a 6-digit rate or a tax%
// header at 80/46/88px is exactly the "…" clipping bug already found and
// fixed elsewhere, just not yet checked here.
// per widened from 40 -- too narrow for a full unit NAME here (not just a
// 2-3 char symbol), which was clipping to e.g. "M…" on a real print.
const COL_WIDTH = { hash: 36, rate: 100, per: 62, qty: 44, disc: 72, tax: 54, total: 104, orderQty: 90, soldQty: 85, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemCell({ name, maxChars, description, bonus }) {
  const fit = fitItemName(name, { maxChars });
  return (
    <div className="da-item-cell">
      <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>
        {fit.text}
        {bonus && <span className="bonus-badge">BONUS</span>}
      </span>
      {description && <div className="da-item-sub">{description}</div>}
    </div>
  );
}

function PaymentTermsPage({ record, companyName }) {
  if (!(record.show_terms_conditions === "yes" && record.payment_terms_description)) return null;
  const lines = record.payment_terms_description.split("\n").filter((l) => l.trim());
  return (
    <div className="da-root">
      <div className="da-page">
        <div className="da-header-band-slim">
          <div className="da-header-left"><p className="da-company-name">{companyName}</p></div>
          <div className="da-header-right"><p className="da-doc-title">PAYMENT TERMS</p></div>
        </div>
        <div className="da-content">
          <div style={{ paddingTop: 30, maxWidth: 620, margin: "0 auto" }}>
            <h4 style={{ textAlign: "center" }}>Payment Terms</h4>
            <ul style={{ lineHeight: 1.8 }}>
              {lines.map((l, i) => <li key={i}>{l.replace(/^•\s*/, "")}</li>)}
            </ul>
          </div>
          <div className="da-spacer" />
        </div>
      </div>
    </div>
  );
}

export default function PurchaseOrderVoucherBodyDesignA({ pur_o_id, showStamp = false, showSignature = false }) {
  const [record, setRecord] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!pur_o_id) return;
    purchaseOrderRecordnWithDetailsApi({ pur_o_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setRecord(res.data[0]);
    });
  }, [pur_o_id]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!record) return null;

  const useBranch = !!authInfo?.branch_info;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  if (record.approval_status === "pending") {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h4>Pending Approval</h4>
        <p>This purchase order is pending approval and cannot be downloaded or printed yet.</p>
      </div>
    );
  }

  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const isIndividual = record.is_cal_type === "individual";

  const uniqueItemsMap = new Map();
  (record.details || []).forEach((item) => {
    const key = `${item.pur_d_id}-${item.item_id}`;
    if (!uniqueItemsMap.has(key)) uniqueItemsMap.set(key, item);
  });
  const items = Array.from(uniqueItemsMap.values()).map((item) => ({
    ...item,
    qty_display: `${formatPercent(item.item_qty)} ${item.unit_symbol}` + (item.conversion > 1 ? `, ${formatPercent(item.retail_qty)} ${item.base_unit_name}` : ""),
    done_qty_display: `${formatPercent(item.done_item_qty || 0)} ${item.unit_symbol}` + (item.conversion > 1 ? `, ${formatPercent(item.done_retail_qty || 0)} ${item.base_unit_name}` : ""),
  }));

  const serviceCost = (record.otherServiceTrans || []).reduce((sum, row) => sum + Number(row.tran_amount || 0), 0);
  const hasBonusItems = items.some(isBonusItem);

  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per +
    (isIndividual
      ? COL_WIDTH.qty + COL_WIDTH.disc + (!isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.total
      : COL_WIDTH.orderQty + COL_WIDTH.soldQty + COL_WIDTH.amount);
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [{ label: "#", width: `${COL_WIDTH.hash}px` }, { label: "Item", cellClassName: "da-item-td" }];
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Per", align: "right", width: `${COL_WIDTH.per}px` });
  if (isIndividual) {
    columns.push({ label: "Qty", align: "right", width: `${COL_WIDTH.qty}px` });
    // Not "Disc %" -- a row's discount can be entered as either a % or a
    // flat amount (discount_mode), so the header stays mode-neutral and
    // each row's own value (formatItemDiscountCell) says which one it is.
    // "Disct" matches the legacy entry screen's own abbreviation for this.
    columns.push({ label: "Disct", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: taxLabel, align: "right", width: `${COL_WIDTH.tax}px` });
    columns.push({ label: "Total", align: "right", width: `${COL_WIDTH.total}px` });
  } else {
    columns.push({ label: "Order Quantity", align: "right", width: `${COL_WIDTH.orderQty}px` });
    columns.push({ label: "Sold Quantity", align: "right", width: `${COL_WIDTH.soldQty}px` });
    columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });
  }

  const rows = items.map((d, i) => {
    const bonus = isBonusItem(d);
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} description={d.item_description} bonus={bonus} />,
    ];
    cells.push(<span className="da-num-cell" key="r">{Number(d.item_rate || 0).toFixed(2)}</span>);
    cells.push(isIndividual ? d.unit_name : d.per_unit_symbol);

    let rawAmount;
    if (isIndividual) {
      cells.push(d.qty_display);
      cells.push(formatItemDiscountCell(d, numberFormat));
      if (!isGSTDisabled) cells.push(formatPercent(d.item_tax_per));
      let total = Number(d.item_qty || 0) * Number(d.item_rate || 0);
      total -= resolveItemDiscountAmount(d, total);
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      cells.push(d.qty_display);
      cells.push(d.done_qty_display);
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: bonus, rawAmount };
  });

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number(d.item_qty || 0);
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = resolveItemDiscountAmount(d, base);
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;
      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      acc.totalQty += qty;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0, totalQty: 0 }
  );
  const grandTotalInWords = (isIndividual ? totals.grandTotal : record.total_amount || 0) + serviceCost;
  const totalTax = isIndividual ? totals.taxTotal : Number(record.total_tax || 0);

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(isIndividual ? totals.subTotal : record.sub_total) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(isIndividual ? totals.discountTotal : record.total_discount)}`, credit: true });
  totalsRows.push({ label: taxLabel, value: numberFormat(totalTax) });
  if (serviceCost > 0) totalsRows.push({ label: "Other Expenses", value: numberFormat(serviceCost) });

  const bankDetails = bankAccountToConfig(primaryBankAccount);

  const config = {
    docTitle: "PURCHASE ORDER",
    docNumber: record.pur_order_no,
    institution: { logoUrl: useBranch ? institution?.branch_logo : institution?.logo, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Vendor",
      billTo: {
        name: record.acc_name,
        lines: [
          record.acc_code ? `Supplier Code: ${record.acc_code}` : null,
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: ${record.contact_no}` : null,
          record.licence_no ? `License No: ${record.licence_no}` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: { name: companyName, lines: [institutionData?.street_address || null, institutionData?.phone_no ? `+${institutionData.phone_no}` : null].filter(Boolean) },
      metaLabel: "Order Details",
      meta: [
        { label: "Order Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        { label: "Order By", value: (record.full_name || "").split(" ")[0] },
        ...(record.payment_terms ? [{ label: "Payment Terms", value: record.payment_terms }] : []),
        ...(institutionData?.vat_license ? [{ label: taxRegLabel, value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: totals.totalQty,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(record.total_amount),
    paidValue: numberFormat(record.paid_total || 0),
    dueValue: numberFormat(record.due_total || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotalInWords, { country, currency }),
    bankDetails,
    taxSummary: null,
    terms: institutionData?.invoice_default_terms || record.narration || "-",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: hasBonusItems ? "*Bonus items are supplied free of cost and not included in taxable value." : null,
    rowBudgets: PURCHASE_ORDER_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return (
    <>
      <VoucherBodyDesignA config={config} />
      <PaymentTermsPage record={record} companyName={companyName} />
    </>
  );
}
